import type { Dayjs } from 'dayjs'
import CondolencesIcon from '@/assets/CondolencesIcon'
import { DetailCard, getDetailWellClass } from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import {
  formatRelativeCalendarDate,
  formatScheduledFulfillmentDate,
} from '@/components/orders/ScheduledFulfillmentWell'
import dayjs from '@/lib/dayjs'
import { readProperty, toTitleCase } from '@/lib/utils'

function CondolencesMemorialCard({ metadata = {} }: { metadata?: unknown }) {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
  const orderNumber = String(readProperty(metadata, 'order_number') ?? '').trim()
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
  const decedentName = String(readProperty(readProperty(metadata, 'decedent'), 'name') ?? '').trim()
  const dateOfDeath = String(
    // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
    readProperty(readProperty(metadata, 'decedent'), 'date_of_death') ?? ''
  ).trim()
  const funeralHomeName = String(
    // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
    readProperty(readProperty(metadata, 'funeral_home'), 'name') ?? ''
  ).trim()
  const delivery = readProperty(metadata, 'delivery') ?? {}
  const deliveryType = formatCondolencesDeliveryType(readProperty(delivery, 'type'))
  const scheduledService = formatCondolencesScheduledService(delivery)

  if (
    !orderNumber &&
    !decedentName &&
    !dateOfDeath &&
    !funeralHomeName &&
    !deliveryType &&
    !scheduledService.value
  ) {
    return null
  }

  return (
    <DetailCard accent="brand" className="lg:col-span-2" contentClassName="space-y-4 p-5">
      <div className="flex items-start gap-3">
        <CondolencesIcon className="mt-0.5 size-5" aria-hidden="true" />
        <div className="min-w-0">
          <Typography variant="eyebrow">Memorial details</Typography>
          <Typography variant="h4" className="mt-1 pb-0">
            {decedentName || funeralHomeName || 'Condolences order'}
          </Typography>
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CondolencesMemorialDetail label="Condolences #" value={orderNumber} />
        <CondolencesMemorialDetail label="Decedent" value={decedentName} />
        <CondolencesMemorialDetail label="Date of death" value={dateOfDeath} />
        <CondolencesMemorialDetail label="Funeral home" value={funeralHomeName} />
        <CondolencesMemorialDetail label="Delivery type" value={deliveryType} />
        <CondolencesMemorialDetail
          label="Scheduled service"
          value={scheduledService.value}
          secondary={scheduledService.secondary}
        />
      </dl>
    </DetailCard>
  )
}

function CondolencesMemorialDetail({
  label,
  value,
  secondary = null,
}: {
  label: string
  value: string
  secondary?: string | null
}) {
  if (!value) {
    return null
  }

  return (
    <div className={getDetailWellClass({ compact: true, tone: 'amethyst' })}>
      <dt>
        <Typography variant="eyebrow">{label}</Typography>
      </dt>
      <dd className="mt-1 min-w-0 text-sm leading-5 text-foreground break-words">
        {value}
        {secondary ? (
          <Typography variant="supporting" className="ml-1">
            {secondary}
          </Typography>
        ) : null}
      </dd>
    </div>
  )
}

function formatCondolencesDeliveryType(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
  const rawValue = String(value ?? '').trim()

  if (!rawValue) {
    return ''
  }

  return toTitleCase(rawValue.replaceAll('_', ' '))
}

function formatCondolencesScheduledService(
  delivery: unknown = {},
  today: Dayjs = dayjs()
): { value: string; secondary: string | null } {
  const serviceDate =
    firstPresentString(
      readProperty(delivery, 'service_date'),
      readProperty(readProperty(delivery, 'service'), 'date'),
      readProperty(delivery, 'service_at'),
      readProperty(delivery, 'service_datetime')
    ) ||
    (hasExplicitCondolencesService(delivery)
      ? firstPresentString(readProperty(delivery, 'date'))
      : '')
  const serviceTime = formatCondolencesServiceTime(
    firstPresentString(
      readProperty(delivery, 'service_time'),
      readProperty(readProperty(delivery, 'service'), 'time'),
      readProperty(delivery, 'service_at'),
      readProperty(delivery, 'service_datetime')
    )
  )
  const serviceDateLabel = formatCondolencesMemorialDeliveryDate(serviceDate)
  const relativeServiceDate = formatCondolencesRelativeDeliveryDate(serviceDate, today)

  if (!serviceDateLabel && !serviceTime) {
    return { value: '', secondary: null }
  }

  return {
    value:
      serviceDateLabel && serviceTime
        ? `${serviceDateLabel} at ${serviceTime}`
        : serviceDateLabel || serviceTime,
    secondary: serviceDateLabel ? relativeServiceDate : null,
  }
}

function hasExplicitCondolencesService(delivery: unknown = {}): boolean {
  const service = readProperty(delivery, 'service')

  return Boolean(
    firstPresentString(
      readProperty(delivery, 'service_time'),
      readProperty(delivery, 'service_date'),
      readProperty(delivery, 'service_at'),
      readProperty(delivery, 'service_datetime')
    ) ||
    (service && typeof service === 'object' && Object.keys(service).length > 0)
  )
}

function firstPresentString(...values: unknown[]): string {
  for (const value of values) {
    // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
    const text = String(value ?? '').trim()

    if (text) {
      return text
    }
  }

  return ''
}

function formatCondolencesMemorialDeliveryDate(value: string | null | undefined): string {
  return formatScheduledFulfillmentDate(value)
}

function formatCondolencesRelativeDeliveryDate(
  value: string | null | undefined,
  today: Dayjs = dayjs()
): string | null {
  return formatRelativeCalendarDate(value, today)
}

function formatCondolencesServiceTime(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy String coercion of raw Condolences metadata
  const rawValue = String(value ?? '').trim()

  if (!rawValue) {
    return ''
  }

  const meridiemMatch = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap]m)$/i)
  if (meridiemMatch) {
    const hour = Number(meridiemMatch[1])
    const minute = Number(meridiemMatch[2])
    const meridiem = meridiemMatch[3]?.toUpperCase()

    if (hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59) {
      return `${hour}:${String(minute).padStart(2, '0')} ${meridiem}`
    }
  }

  const timeMatch = rawValue.match(/(?:^|[T\s])(\d{1,2}):(\d{2})(?::\d{2})?/)
  if (!timeMatch) {
    return rawValue
  }

  const hour = Number(timeMatch[1])
  const minute = Number(timeMatch[2])

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return rawValue
  }

  const displayHour = hour % 12 || 12
  const meridiem = hour >= 12 ? 'PM' : 'AM'

  return `${displayHour}:${String(minute).padStart(2, '0')} ${meridiem}`
}

export {
  CondolencesMemorialCard,
  formatCondolencesDeliveryType,
  formatCondolencesMemorialDeliveryDate,
  formatCondolencesRelativeDeliveryDate,
  formatCondolencesScheduledService,
}
